import { Card, List, Spin, Typography } from 'antd'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Notification } from '@/types'
import { formatHm, formatYMD } from '@/utils/date'

import { CustomButton, DateWrapper, NotificationContainer, NotificationItem } from './styles'

type Props = {
  dataSource: Notification[] | undefined
  getNotificationsLoading: boolean
  onUpdate: (id: Notification['id']) => void
}

const { Text } = Typography

const NotificationTable = ({ dataSource, getNotificationsLoading, onUpdate }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const groupedNotifications = useMemo(() => {
    if (!dataSource) return []

    const groups: Record<string, typeof dataSource> = {}

    dataSource.forEach((item) => {
      const dateKey = formatYMD(item.createdAt)
      if (!groups[dateKey]) {
        groups[dateKey] = []
      }
      groups[dateKey].push(item)
    })

    return Object.entries(groups).map(([date, notifications]) => ({
      date,
      notifications,
    }))
  }, [dataSource])

  const handleMarkAsRead = useCallback(
    (value: Notification) => {
      onUpdate(value.id)
      if (value.path) {
        navigate(value.path)
      }
    },
    [navigate, onUpdate]
  )

  return (
    <Spin spinning={getNotificationsLoading}>
      <NotificationContainer>
        {groupedNotifications.map(({ date, notifications }) => (
          <Card key={date} bordered={false}>
            <DateWrapper>{date}</DateWrapper>
            <List
              dataSource={notifications}
              renderItem={(item) => (
                <NotificationItem className={item.isRead ? 'highlighted' : ''}>
                  <List.Item.Meta
                    title={
                      <>
                        <Text type="secondary" style={{ marginRight: '24px' }}>{`${formatYMD(
                          item.createdAt
                        )} ${formatHm(item.createdAt)}`}</Text>
                        <Text strong>{item.message}</Text>
                      </>
                    }
                  />
                  {!item.isRead && (
                    <CustomButton onClick={() => handleMarkAsRead(item)}>
                      {t('components.pages.notifications.list.table.exchange')}
                    </CustomButton>
                  )}
                </NotificationItem>
              )}
            />
          </Card>
        ))}
      </NotificationContainer>
    </Spin>
  )
}

NotificationTable.displayName = 'NotificationTable'
export default NotificationTable
